import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { theme } from '../styles/theme';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import SectionTitle from '../components/ui/SectionTitle';
import BackButton from '../components/ui/BackButton';
import CartItemNotice from '../components/cart/CartItemNotice';
import SizeEstimateNote from '../components/cart/SizeEstimateNote';

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems, removeItem, updateQuantity, clearCart, subtotal,
    availability, hasUnavailableItems, recheckAvailability,
  } = useCart();

  // Products can be turned off or sell out while they sit in a cart.
  useEffect(() => {
    recheckAvailability();
  }, [recheckAvailability]);
  const { isAuthenticated, isApproved } = useAuth();
  const [quantityInputs, setQuantityInputs] = useState({});
  const [loadingItems, setLoadingItems] = useState({});

  const containerStyles = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: `${theme.spacing.xxl} ${theme.spacing.lg}`,
    minHeight: '70vh',
  };

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: theme.spacing.xl,
  };

  const itemStyles = {
    display: 'grid',
    gridTemplateColumns: '140px 1fr auto',
    gap: theme.spacing.xl,
    padding: theme.spacing.xl,
    marginBottom: theme.spacing.md,
    transition: 'all 0.3s ease',
  };

  const imageStyles = {
    width: '140px',
    height: '140px',
    objectFit: 'cover',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.surfaceAlt,
    boxShadow: theme.shadows.md,
    border: `1px solid ${theme.colors.borderLight}`,
  };

  const handleQuantityInputChange = (itemKey, value) => {
    // Allow only numbers
    const numValue = value.replace(/[^0-9]/g, '');
    setQuantityInputs(prev => ({ ...prev, [itemKey]: numValue }));
  };

  const handleUpdateQuantity = async (itemKey) => {
    const newQuantity = parseInt(quantityInputs[itemKey] || '0', 10);
    if (newQuantity < 1) {
      // Reset to current quantity if invalid
      const currentItem = cartItems.find(item => 
        (item.cartId || item.cart_id || item.id || item.product_id) === itemKey
      );
      if (currentItem) {
        setQuantityInputs(prev => ({ ...prev, [itemKey]: currentItem.quantity.toString() }));
      }
      return;
    }
    
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }));
    try {
      await updateQuantity(itemKey, newQuantity);
      // Keep the updated quantity in the input
      setQuantityInputs(prev => ({ ...prev, [itemKey]: newQuantity.toString() }));
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleRemoveItem = async (itemKey) => {
    setLoadingItems(prev => ({ ...prev, [itemKey]: true }));
    try {
      await removeItem(itemKey);
    } finally {
      setLoadingItems(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  if (cartItems.length === 0) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <SectionTitle size="xl" align="center">Shopping Cart</SectionTitle>
        <Card 
          padding="xxl" 
          style={{ 
            marginTop: theme.spacing.xl,
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <p style={{ 
            fontSize: '5rem', 
            marginBottom: theme.spacing.lg 
          }}>
            🛒
          </p>
          <h3 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
          }}>
            Your cart is empty
          </h3>
          <p style={{ 
            fontSize: theme.typography.fontSize.lg, 
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing.xxl,
          }}>
            Looks like you haven't added anything to your cart yet
          </p>
          <button
            onClick={() => navigate('/products')}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.xxl}`,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily.secondary,
              background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              boxShadow: '0 4px 12px rgba(204, 85, 0, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
              const arrow = e.currentTarget.querySelector('.shop-arrow');
              if (arrow) arrow.style.transform = 'translateX(6px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
              const arrow = e.currentTarget.querySelector('.shop-arrow');
              if (arrow) arrow.style.transform = 'translateX(0)';
            }}
          >
            <span>Start Shopping</span>
            <span 
              className="shop-arrow"
              style={{
                fontSize: '1.3rem',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
              }}
            >
              →
            </span>
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: theme.colors.background, minHeight: '100vh' }}>
      <div style={containerStyles}>
        <BackButton 
          to="/products"
          label="Continue Shopping"
          variant="ghost"
          style={{ marginBottom: theme.spacing.lg }}
        />
        
        <SectionTitle size="xl" withDivider>
          Shopping Cart ({cartItems.length} items)
        </SectionTitle>

        <div style={gridStyles}>
          {/* Cart Items */}
          <div>
            {cartItems.map((item) => {
              const itemKey = item.cartId || item.cart_id || item.id || item.product_id;
              const imageUrl = item.image_url || item.imageUrl || item.images?.[0] || '';
              const isLoading = loadingItems[itemKey];
              return (
                <Card 
                  key={itemKey} 
                  padding="none" 
                  style={{ 
                    marginBottom: theme.spacing.md,
                    transition: 'all 0.3s ease',
                    border: `1px solid ${theme.colors.borderLight}`,
                    position: 'relative',
                    opacity: isLoading ? 0.6 : 1,
                    pointerEvents: isLoading ? 'none' : 'auto',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = theme.shadows.lg;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = theme.shadows.md;
                    }
                  }}
                >
                  <div style={itemStyles}>
                    {/* Loading Overlay */}
                    {isLoading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: theme.borderRadius.lg,
                        zIndex: 10,
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          border: `4px solid ${theme.colors.borderLight}`,
                          borderTop: `4px solid ${theme.colors.primary}`,
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite',
                        }} />
                      </div>
                    )}
                    
                    {/* Image */}
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={item.name}
                        style={imageStyles}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div style={{
                        ...imageStyles,
                        backgroundColor: '#E5E5E5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }} />
                    )}

                    {/* Details */}
                    <div>
                      <h3 style={{
                        fontFamily: theme.typography.fontFamily.primary,
                        fontSize: theme.typography.fontSize.xl,
                        fontWeight: theme.typography.fontWeight.semibold,
                        marginBottom: theme.spacing.xs,
                        color: '#000000',
                      }}>
                        {item.name}
                      </h3>
                      {(item.variantName || item.subcategory_name) && (
                        <p style={{
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.colors.accent,
                          marginBottom: theme.spacing.xs,
                          fontStyle: 'italic',
                          fontWeight: theme.typography.fontWeight.semibold,
                        }}>
                          Variant: {item.variantName || item.subcategory_name}
                        </p>
                      )}
                      <CartItemNotice status={availability[itemKey]} />
                      <SizeEstimateNote approxSqft={availability[itemKey]?.approx_sqft} />
                      {item.selected_components && item.selected_components.length > 0 && (
                        <div style={{
                          fontSize: theme.typography.fontSize.xs,
                          color: theme.colors.textSecondary,
                          marginBottom: theme.spacing.xs,
                          padding: theme.spacing.xs,
                          backgroundColor: theme.colors.backgroundAlt,
                          borderRadius: theme.borderRadius.sm,
                          borderLeft: `3px solid ${theme.colors.accent}`,
                        }}>
                          <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '2px' }}>
                            + Customizations:
                          </div>
                          {item.selected_components.map((comp, idx) => (
                            <div key={idx} style={{ paddingLeft: theme.spacing.xs }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>• {comp.name}{comp.variation_name ? ` (${comp.variation_name})` : ''}</span>
                                <span style={{ fontWeight: theme.typography.fontWeight.medium }}>
                                  +${(Number(comp.price) || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p style={{
                        color: theme.colors.textSecondary,
                        fontSize: theme.typography.fontSize.sm,
                        marginBottom: theme.spacing.sm,
                      }}>
                        SKU: {item.sku}
                      </p>
                      {/* Price with discount badge */}
                      {(() => {
                        const currentPrice = Number(item.price) || 0;
                        const basePrice = Number(item.base_price_without_discount) || currentPrice;
                        const hasDiscount = Math.abs(currentPrice - basePrice) > 0.01;
                        
                        return (
                          <div style={{ marginBottom: theme.spacing.md }}>
                            {hasDiscount ? (
                              <>
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{
                                    fontSize: theme.typography.fontSize.sm,
                                    color: theme.colors.textSecondary,
                                  }}>
                                    Original Price: 
                                  </span>
                                  <span style={{
                                    fontSize: theme.typography.fontSize.base,
                                    color: theme.colors.textSecondary,
                                    textDecoration: 'line-through',
                                    marginLeft: '6px',
                                    fontWeight: theme.typography.fontWeight.medium,
                                  }}>
                                    ${basePrice.toFixed(2)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                  <span style={{
                                    fontSize: theme.typography.fontSize.lg,
                                    fontWeight: theme.typography.fontWeight.bold,
                                    color: theme.colors.primary,
                                  }}>
                                    ${currentPrice.toFixed(2)} each
                                  </span>
                                  <div style={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    backgroundColor: '#dcfce7',
                                    border: '1px solid #86efac',
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: '0.75rem',
                                    color: '#16a34a',
                                    fontWeight: 600,
                                  }}>
                                    Quantity Discount: {((basePrice - currentPrice) / basePrice * 100).toFixed(0)}% off
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div style={{ marginBottom: '6px' }}>
                                <span style={{
                                  fontSize: theme.typography.fontSize.lg,
                                  fontWeight: theme.typography.fontWeight.bold,
                                  color: theme.colors.primary,
                                }}>
                                  ${currentPrice.toFixed(2)} each
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Quantity Controls */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: theme.spacing.md,
                        flexWrap: 'wrap',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.xs,
                        }}>
                          <label style={{
                            fontSize: theme.typography.fontSize.base,
                            color: theme.colors.text,
                            fontWeight: theme.typography.fontWeight.semibold,
                          }}>
                            Quantity:
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={quantityInputs[itemKey] !== undefined ? quantityInputs[itemKey] : item.quantity}
                            onChange={(e) => handleQuantityInputChange(itemKey, e.target.value)}
                            disabled={isLoading}
                            style={{
                              width: '60px',
                              padding: '8px 10px',
                              border: `2px solid ${theme.colors.border}`,
                              borderRadius: theme.borderRadius.md,
                              textAlign: 'center',
                              fontWeight: theme.typography.fontWeight.bold,
                              fontSize: theme.typography.fontSize.base,
                              backgroundColor: theme.colors.surface,
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateQuantity(itemKey);
                              }
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleUpdateQuantity(itemKey)}
                          disabled={isLoading || !quantityInputs[itemKey]}
                          style={{
                            padding: '8px 16px',
                            border: 'none',
                            backgroundColor: theme.colors.primary,
                            color: '#fff',
                            cursor: quantityInputs[itemKey] ? 'pointer' : 'not-allowed',
                            fontWeight: theme.typography.fontWeight.bold,
                            fontSize: theme.typography.fontSize.base,
                            borderRadius: theme.borderRadius.md,
                            transition: 'all 0.2s ease',
                            opacity: quantityInputs[itemKey] ? 1 : 0.5,
                          }}
                          onMouseEnter={(e) => {
                            if (quantityInputs[itemKey]) {
                              e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = theme.colors.primary;
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          Update
                        </button>
                        <button
                          onClick={() => handleRemoveItem(itemKey)}
                          disabled={isLoading}
                          style={{ 
                            color: theme.colors.error,
                            marginLeft: theme.spacing.sm,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: theme.typography.fontSize.base,
                            fontWeight: theme.typography.fontWeight.semibold,
                            textDecoration: 'underline',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = theme.colors.primaryDark;
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = theme.colors.error;
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Item Total */}
                    <div style={{ textAlign: 'right' }}>
                      <p style={{
                        fontSize: theme.typography.fontSize['2xl'],
                        fontWeight: theme.typography.fontWeight.bold,
                        color: theme.colors.primary,
                      }}>
                        ${((Number(item.price) || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}

            {/* Clear Cart Button */}
            <button
              onClick={clearCart}
              style={{ 
                marginTop: theme.spacing.md,
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                fontFamily: theme.typography.fontFamily.secondary,
                background: 'transparent',
                color: theme.colors.error,
                border: `2px solid ${theme.colors.error}`,
                borderRadius: theme.borderRadius.lg,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.error;
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = theme.shadows.md;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = theme.colors.error;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Clear Cart
            </button>
          </div>

          {/* Order Summary */}
          <div>
            <Card 
              padding="xl" 
              style={{ 
                position: 'sticky', 
                top: '100px',
                background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                border: `1px solid ${theme.colors.borderLight}`,
                boxShadow: theme.shadows.xl,
              }}
            >
              <h3 style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize['2xl'],
                fontWeight: theme.typography.fontWeight.bold,
                marginBottom: theme.spacing.xl,
                paddingBottom: theme.spacing.md,
                borderBottom: `2px solid ${theme.colors.primary}`,
                color: theme.colors.text,
              }}>
                Order Summary
              </h3>

              <div style={{ marginBottom: theme.spacing.lg }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing.md,
                }}>
                  <span style={{ color: theme.colors.textSecondary }}>
                    Subtotal
                  </span>
                  <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing.md,
                }}>
                  <span style={{ color: theme.colors.textSecondary }}>
                    Estimated Shipping
                  </span>
                  <span style={{ color: theme.colors.textSecondary }}>
                    Calculated at checkout
                  </span>
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: theme.spacing.md,
                }}>
                  <span style={{ color: theme.colors.textSecondary }}>
                    Tax
                  </span>
                  <span style={{ color: theme.colors.textSecondary }}>
                    Calculated at checkout
                  </span>
                </div>

                <div style={{
                  borderTop: `2px solid ${theme.colors.border}`,
                  paddingTop: theme.spacing.lg,
                  marginTop: theme.spacing.lg,
                  backgroundColor: theme.colors.surface,
                  padding: theme.spacing.lg,
                  borderRadius: theme.borderRadius.lg,
                  marginLeft: `-${theme.spacing.lg}`,
                  marginRight: `-${theme.spacing.lg}`,
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    fontSize: theme.typography.fontSize['2xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    fontFamily: theme.typography.fontFamily.primary,
                  }}>
                    <span style={{ color: theme.colors.text }}>Subtotal</span>
                    <span style={{ color: theme.colors.primary }}>
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {!isAuthenticated ? (
                <Card 
                  padding="md" 
                  style={{ 
                    backgroundColor: theme.colors.info, 
                    color: '#fff',
                    textAlign: 'center',
                    marginBottom: theme.spacing.sm,
                  }}
                >
                  <p style={{ fontSize: theme.typography.fontSize.sm, marginBottom: theme.spacing.sm }}>
                    Please sign in to checkout
                  </p>
                  <Button 
                    size="sm" 
                    fullWidth
                    variant="outline"
                    onClick={() => navigate('/')}
                    style={{ 
                      borderColor: '#fff',
                      color: '#fff',
                    }}
                  >
                    Sign In
                  </Button>
                </Card>
              ) : !isApproved ? (
                <Card 
                  padding="md" 
                  style={{ 
                    backgroundColor: theme.colors.warning, 
                    textAlign: 'center',
                  }}
                >
                  <p style={{ fontSize: theme.typography.fontSize.sm }}>
                    Your account needs approval before checkout
                  </p>
                </Card>
              ) : (
                <>
                  {hasUnavailableItems && (
                    <p role="status" style={{
                      margin: `0 0 ${theme.spacing.md}`,
                      fontSize: theme.typography.fontSize.sm,
                      color: '#991B1B',
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}>
                      Some items in your cart can’t be ordered right now. Update or remove them to check out.
                    </p>
                  )}
                  <button
                    onClick={() => navigate('/checkout')}
                    disabled={hasUnavailableItems}
                    style={{
                      width: '100%',
                      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                      fontSize: theme.typography.fontSize.lg,
                      fontWeight: theme.typography.fontWeight.bold,
                      fontFamily: theme.typography.fontFamily.secondary,
                      background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
                      color: '#fff',
                      border: 'none',
                      borderRadius: theme.borderRadius.lg,
                      boxShadow: hasUnavailableItems ? 'none' : '0 4px 12px rgba(204, 85, 0, 0.3)',
                      cursor: hasUnavailableItems ? 'not-allowed' : 'pointer',
                      opacity: hasUnavailableItems ? 0.5 : 1,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: theme.spacing.sm,
                      marginBottom: theme.spacing.md,
                    }}
                    onMouseEnter={(e) => {
                      if (hasUnavailableItems) return;
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
                      const arrow = e.currentTarget.querySelector('.checkout-arrow');
                      if (arrow) arrow.style.transform = 'translateX(6px)';
                    }}
                    onMouseLeave={(e) => {
                      if (hasUnavailableItems) return;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
                      const arrow = e.currentTarget.querySelector('.checkout-arrow');
                      if (arrow) arrow.style.transform = 'translateX(0)';
                    }}
                  >
                    <span>Proceed to Checkout</span>
                    <span 
                      className="checkout-arrow"
                      style={{
                        fontSize: '1.3rem',
                        transition: 'transform 0.3s ease',
                        display: 'inline-block',
                      }}
                    >
                      →
                    </span>
                  </button>
                  <button
                    onClick={() => navigate('/products')}
                    style={{
                      width: '100%',
                      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                      fontSize: theme.typography.fontSize.base,
                      fontWeight: theme.typography.fontWeight.semibold,
                      fontFamily: theme.typography.fontFamily.secondary,
                      background: 'transparent',
                      color: theme.colors.primary,
                      border: `2px solid ${theme.colors.primary}`,
                      borderRadius: theme.borderRadius.lg,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary;
                      e.currentTarget.style.color = '#fff';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = theme.shadows.md;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = theme.colors.primary;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Continue Shopping
                  </button>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 2fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
          div[style*="grid-template-columns: 120px 1fr auto"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CartPage;

